import { queryOptions, useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { dataSyncKeys } from '../query-keys';
import { useAuthStore } from '@/stores/auth-store';
import {
  showDataSyncContract,
  showPersonioSetupRowsContract,
  showDataSyncRunContract,
  type DataSyncStatus,
  type DataSyncRun,
  type ShowPersonioSetupRowsResponse
} from '@bliks-io/contracts';

/** The Personio link, each source's last run, and the open run. */
export function useDataSyncStatus() {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: dataSyncKeys.status(),
    queryFn: async ({ signal }): Promise<DataSyncStatus> => {
      const { data } = await apiClient.get<DataSyncStatus>(showDataSyncContract.path, {
        signal
      });
      return data;
    },
    enabled: !!token,
    // Polled while a run reads or applies, so its end frees the start buttons without a reload.
    refetchInterval: (query) => {
      const status = query.state.data?.openRun?.status;
      return status === 'reading' || status === 'applying' ? 3000 : false;
    }
  });
}

/** A sync run with its differences and outcomes. */
export function useDataSyncRun(id: string, { enabled = true } = {}) {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: dataSyncKeys.syncRun(id),
    queryFn: async ({ signal }): Promise<DataSyncRun> => {
      const { data } = await apiClient.get<DataSyncRun>(
        showDataSyncRunContract.path.replace(':id', id),
        { signal }
      );
      return data;
    },
    enabled: !!token && enabled
  });
}

/**
 * Query options behind {@link usePersonioSetupRows}, shared with the setup
 * read that fetches the fresh rows. Never stale: the rows change only on the
 * setup read, which fetches them, and on completion, which removes them.
 */
export function personioSetupRowsQueryOptions() {
  return queryOptions({
    queryKey: dataSyncKeys.setupRows(),
    queryFn: async ({ signal }): Promise<ShowPersonioSetupRowsResponse> => {
      const { data } = await apiClient.get<ShowPersonioSetupRowsResponse>(
        showPersonioSetupRowsContract.path,
        { signal }
      );
      return data;
    },
    staleTime: Infinity,
    gcTime: Infinity
  });
}

/**
 * The initial-setup rows seeded from the stored data-sync snapshot. Only
 * a Personio read stores one, so nothing is fetched while Personio is not linked.
 */
export function usePersonioSetupRows() {
  const { token } = useAuthStore();
  const { data: status } = useDataSyncStatus();

  return useQuery({
    ...personioSetupRowsQueryOptions(),
    enabled: !!token && !!status?.personio
  });
}
